import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import PathfinderSingleSelectDropdown from './PathfinderSingleSelectDropdown';

// Level 드롭다운이 비활성화되는 자격증 코드
const NO_LEVEL_CERT_CODES = new Set(['LANG_NONE', 'LANG_TOPIK5']);
const NO_APPEND_CERT_CODES = new Set(['LANG_NONE']);

const EMPTY_ROW = { certType: '', level: '' };

const isRowComplete = (row) => {
  if (!row?.certType) return false;
  return NO_LEVEL_CERT_CODES.has(row.certType) || Boolean(row.level);
};

const shouldAppendEmptyRow = (row) =>
  isRowComplete(row) && !NO_APPEND_CERT_CODES.has(row?.certType);

/**
 * Q_LANG_SCORE 전용 — 자격증 종류·레벨 쌍을 여러 행 입력하는 컴포넌트
 *
 * - 행이 완료되면 자동으로 새 빈 행 추가
 * - options(BE raw) description 필드로 분류:
 *     description = null → Certification Type 드롭다운
 *     description = 'LANG_TOPIK1' | 'LANG_TOPIK3' → Level 드롭다운 (certType별)
 *
 * @param {{ optionCode, optionLabel, description }[]} options  BE raw 옵션 배열
 * @param {Array<{ certType: string, level: string }>} value    현재 행 배열
 * @param {Function} onChange  (rows) => void
 */
function PathfinderLangScorePicker({ options = [], value, onChange }) {
  const [rows, setRows] = useState(() => {
    if (!Array.isArray(value) || value.length === 0) return [{ ...EMPTY_ROW }];
    const last = value[value.length - 1];
    // 마지막 행이 완료되어 있으면 빈 행 추가
    return shouldAppendEmptyRow(last) ? [...value, { ...EMPTY_ROW }] : [...value];
  });

  const { certOptions, levelOptionsMap } = useMemo(() => {
    console.log('[LangScorePicker] options received:', options);
    const cert = [];
    const levelMap = {};
    options.forEach((opt) => {
      const item = { value: opt.optionCode, label: opt.optionLabel };
      // description이 null/undefined/빈 문자열이면 Certification Type 옵션
      if (opt.description == null || opt.description === '') {
        cert.push(item);
      } else {
        if (!levelMap[opt.description]) levelMap[opt.description] = [];
        levelMap[opt.description].push(item);
      }
    });
    console.log('[LangScorePicker] certOptions:', cert, 'levelMap keys:', Object.keys(levelMap));
    return { certOptions: cert, levelOptionsMap: levelMap };
  }, [options]);

  const updateRow = (idx, patch) => {
    const newRows = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    const updated = newRows[idx];
    const isLast = idx === newRows.length - 1;

    // 마지막 행이 완료되면 빈 행 추가
    if (isLast && shouldAppendEmptyRow(updated)) {
      newRows.push({ ...EMPTY_ROW });
    }

    setRows(newRows);
    // 빈 미완성 마지막 행은 저장 값에서 제외
    const saveRows = newRows.filter((r, i) => i < newRows.length - 1 || isRowComplete(r));
    onChange?.(saveRows);
  };

  const handleCertChange = (idx, certType) => {
    updateRow(idx, { certType, level: '' });
  };

  const handleLevelChange = (idx, level) => {
    updateRow(idx, { level });
  };

  return (
    <div className='flex w-full flex-col gap-4'>
      {rows.map((row, idx) => {
        const levelOptions = levelOptionsMap[row.certType] ?? [];
        const isLevelDisabled = !row.certType || NO_LEVEL_CERT_CODES.has(row.certType);

        return (
          <div key={idx} className='flex w-full gap-4'>
            <div className='flex-1'>
              <PathfinderSingleSelectDropdown
                options={certOptions}
                value={row.certType}
                onChange={(v) => handleCertChange(idx, v)}
                placeholder='Certification Type'
                visibleOptionCount={6}
              />
            </div>
            <div className={twMerge('flex-1', isLevelDisabled && 'pointer-events-none opacity-40')}>
              <PathfinderSingleSelectDropdown
                options={levelOptions}
                value={row.level}
                onChange={(v) => handleLevelChange(idx, v)}
                placeholder='Level'
                visibleOptionCount={6}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default PathfinderLangScorePicker;
